import { ref } from 'vue';
import { defineStore } from 'pinia';

export const features = {
  analytics: 'analytics',
  batchCollections: 'money.batchPayments',
  batchPayments: 'money.batchPayments.outgoing',
  contacts: 'contacts',
  controlSpending: 'controlSpending',
  darkMode: 'darkMode',
  exchange: 'exchange',
  accounts: 'accounts',
  ibansSwiper: 'ibansSwiper',
  money: 'money',
  notifications: 'notifications',
  paymentsApproval: 'paymentsApproval',
  paymentTags: 'payment.tags',
  settings: 'settings',
  settingsNotifications: 'settings.notifications',
  settingsUserControl: 'settings.userControl',
  swiftBeneficiaryCreation: 'beneficiaries.swiftCreation',
  totalBalance: 'totalBalance',
  transactionDetails: 'transactionDetails',
  dashboardTotalBalanceCard: 'dashboard.totalBalanceCard',
} as const;

export const featureCustomizations = {
  sepaBeneficiaryBicVisible: 'customizations.beneficiaries.sepaBicVisible',
  beneficiaryMultipleBankAccountsEnabled:
    'customizations.beneficiaries.multipleBankAccountsEnabled',
} as const;

export type FeatureKey = (typeof features)[keyof typeof features];
export type FeatureFlags = Partial<Record<FeatureKey, boolean>>;
export type FeatureCustomizationKey =
  (typeof featureCustomizations)[keyof typeof featureCustomizations];
export type FeatureCustomizations = Partial<Record<FeatureCustomizationKey, boolean>>;

export interface FeatureConfig {
  flags?: FeatureFlags;
  customizations?: FeatureCustomizations;
  /**
   * Deployment-level overrides, for example flags parsed from VITE_FEATURE_FLAGS.
   * Applied after customer defaults so the same customer can differ by environment.
   */
  overrides?: FeatureFlags;
  customizationOverrides?: FeatureCustomizations;
}

/**
 * Customer/app feature configuration that can be sourced from:
 * - Hardcoded app defaults
 * - Build-time deployment env overrides
 * - API or remote providers later
 */
export const useFeatureManagerStore = defineStore('feature-manager', () => {
  const flags = ref<FeatureFlags>({});
  const customizations = ref<FeatureCustomizations>({});

  const initialize = (config: FeatureConfig = {}) => {
    flags.value = { ...(config.flags ?? {}), ...(config.overrides ?? {}) };
    customizations.value = {
      ...(config.customizations ?? {}),
      ...(config.customizationOverrides ?? {}),
    };
  };

  const mergeFlags = (newFlags: FeatureFlags = {}) => {
    flags.value = { ...flags.value, ...newFlags };
  };

  const mergeCustomizations = (newCustomizations: FeatureCustomizations = {}) => {
    customizations.value = { ...customizations.value, ...newCustomizations };
  };

  const isEnabled = (key: FeatureKey) => Boolean(flags.value[key]);

  const getCustomization = (key: FeatureCustomizationKey, defaultValue = false) =>
    customizations.value[key] ?? defaultValue;

  const refreshFromProviders = async () => {
    // TODO: Load and merge remote providers when available.
  };

  return {
    flags,
    customizations,
    initialize,
    mergeFlags,
    mergeCustomizations,
    isEnabled,
    getCustomization,
    refreshFromProviders,
  };
});
